import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { 
  Users, Car, Drop, CurrencyCircleDollar, ChartLineUp, 
  CaretLeft, PencilSimple, Trash, Crown
} from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import MarketingPanel from '../components/MarketingPanel';
import AdminSubscribersTab from '../components/AdminSubscribersTab';
import CustomersTab from '../components/CustomersTab';
import PlansManagerTab from '../components/PlansManagerTab';
import AdminBookingsTab from '../components/AdminBookingsTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { user, isSuperAdmin } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const staffUsers = useMemo(() => users.filter((u) => u.role === 'staff' || u.role === 'location_manager'), [users]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [canMarketing, setCanMarketing] = useState(false);
  const [canCustomers, setCanCustomers] = useState(false);
  
  // Edit dialogs
  const [editProgram, setEditProgram] = useState(null);
  const [editPlan, setEditPlan] = useState(null);
  // Delete user dialog
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Iter16 Task 9 — Stafi / Klientët sub-tab + bulk delete
  const [userSubTab, setUserSubTab] = useState('staff');
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/users/${userToDelete.user_id}`, { withCredentials: true });
      toast.success(t('autoSaved'));
      setUserToDelete(null);
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed';
      if (err.response?.status === 409) {
        toast.error(t('deleteUserBlocked'));
      } else {
        toast.error(detail);
      }
    } finally {
      setDeleting(false);
    }
  };

  // Iter20 Task 9 — Reset test-data handler removed (button removed from UI too).

  useEffect(() => {
    fetchData();
    // Detect if current user can view marketing list — owner always, manager/employee only when flagged.
    axios
      .get(`${API}/me/permissions`, { withCredentials: true })
      .then((r) => {
        const perms = r.data?.permissions || {};
        setCanMarketing(Boolean(r.data?.is_super_admin || perms.view_marketing));
        // Iter23 B2 — manager-shared Klientët tab gated by `view_customers` perm.
        setCanCustomers(Boolean(r.data?.is_super_admin || perms.view_customers));
      })
      .catch(() => {});
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, programsRes, plansRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/wash-programs`),
        axios.get(`${API}/subscription-plans`),
        axios.get(`${API}/admin/users`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setPrograms(programsRes.data);
      setPlans(plansRes.data);
      setUsers(usersRes.data.users);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProgram = async () => {
    try {
      await axios.put(`${API}/wash-programs/${editProgram.program_id}`, editProgram, { withCredentials: true });
      toast.success('Program updated');
      setEditProgram(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleUpdatePlan = async () => {
    try {
      await axios.put(`${API}/subscription-plans/${editPlan.plan_id}`, editPlan, { withCredentials: true });
      toast.success('Plan updated');
      setEditPlan(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const statCards = [
    { label: t('totalUsers'), value: stats?.total_users || 0, icon: Users, color: '#0ABAB5' },
    { label: t('activeSubscriptions'), value: stats?.active_subscriptions || 0, icon: ChartLineUp, color: '#FF5A00' },
    { label: t('todayWashes'), value: stats?.today_washes || 0, icon: Drop, color: '#0ABAB5' },
    // Iter16 Task 8 — daily cash KPI
    { label: t('todayRevenue'), value: `${(stats?.today_revenue || 0).toLocaleString()} L`, icon: CurrencyCircleDollar, color: '#FFD700' },
    { label: t('totalCars'), value: stats?.total_cars || 0, icon: Car, color: '#FFD700' },
    { label: t('monthlyRevenue'), value: `${(stats?.monthly_revenue || 0).toLocaleString()} L`, icon: CurrencyCircleDollar, color: '#0ABAB5' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0ABAB5] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pb-8">
      {/* Header */}
      <div className="p-4 pt-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            className="text-white/50 hover:text-white"
          >
            <CaretLeft size={24} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight font-['Outfit'] text-white">{t('admin')} {t('dashboard')}</h1>
            <p className="text-white/50 text-sm">AutoSpa5 Management</p>
          </div>
        </div>
        {isSuperAdmin && (
          <Button
            onClick={() => navigate('/owner-console')}
            className="h-10 px-3 bg-[#FFD700] hover:bg-[#FFD700]/90 text-black font-semibold rounded-xl text-xs"
            data-testid="open-owner-console"
          >
            <Crown size={14} weight="fill" className="mr-1.5" />
            {t('ownerConsole')}
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="px-4">
        <TabsList className="w-full bg-[#121212] border border-white/5 rounded-2xl p-1 h-auto">
          <TabsTrigger value="dashboard" className="flex-1 rounded-xl data-[state=active]:bg-[#0ABAB5] data-[state=active]:text-black" data-testid="admin-tab-dashboard">
            {t('dashboard')}
          </TabsTrigger>
          <TabsTrigger value="programs" className="flex-1 rounded-xl data-[state=active]:bg-[#0ABAB5] data-[state=active]:text-black" data-testid="admin-tab-programs">
            {t('programs')}
          </TabsTrigger>
          <TabsTrigger value="plans" className="flex-1 rounded-xl data-[state=active]:bg-[#0ABAB5] data-[state=active]:text-black" data-testid="admin-tab-plans">
            {t('plansTab')}
          </TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="users" className="flex-1 rounded-xl data-[state=active]:bg-[#0ABAB5] data-[state=active]:text-black" data-testid="admin-tab-users">
              {t('staffTab')}
            </TabsTrigger>
          )}
          {isSuperAdmin && (
            <TabsTrigger value="subscribers" className="flex-1 rounded-xl data-[state=active]:bg-[#0ABAB5] data-[state=active]:text-black" data-testid="admin-tab-subscribers">
              {t('subscribersTab')}
            </TabsTrigger>
          )}
          {canMarketing && (
            <TabsTrigger value="marketing" className="flex-1 rounded-xl data-[state=active]:bg-[#0ABAB5] data-[state=active]:text-black" data-testid="admin-tab-marketing">
              {t('marketingTab')}
            </TabsTrigger>
          )}
          {canCustomers && (
            <TabsTrigger value="customers" className="flex-1 rounded-xl data-[state=active]:bg-[#0ABAB5] data-[state=active]:text-black" data-testid="admin-tab-customers">
              {t('totalUsersTab')}
            </TabsTrigger>
          )}
          {/* Iter33 — Detailing bookings management moved here from the retired /manager panel. */}
          {isSuperAdmin && (
            <TabsTrigger value="bookings" className="flex-1 rounded-xl data-[state=active]:bg-[#0ABAB5] data-[state=active]:text-black" data-testid="admin-tab-bookings">
              Prenotimet
            </TabsTrigger>
          )}
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="mt-4">
          <div className="grid grid-cols-2 gap-3">
            {statCards.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`p-4 rounded-2xl bg-[#121212] border border-white/5 ${index === 4 ? 'col-span-2' : ''}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${stat.color}20` }}
                  >
                    <stat.icon size={20} weight="duotone" style={{ color: stat.color }} />
                  </div>
                </div>
                <p className="text-2xl font-semibold font-['Outfit']" style={{ color: stat.color }}>
                  {stat.value}
                </p>
                <p className="text-white/50 text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </div>

          {/* Revenue by Type */}
          {stats?.revenue_by_type && Object.keys(stats.revenue_by_type).length > 0 && (
            <motion.div
              className="mt-4 p-4 rounded-2xl bg-[#121212] border border-white/5"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <h3 className="font-medium mb-3">{t('revenueByType')}</h3>
              <div className="space-y-2">
                {Object.entries(stats.revenue_by_type).map(([type, amount]) => {
                  // Iter15 Task 10 — translate the keys instead of capitalising raw strings.
                  const label = t(`txType_${type}`) || type.replace(/_/g, ' ');
                  return (
                    <div key={type} className="flex justify-between items-center">
                      <span className="text-white/60 capitalize">{label}</span>
                      <span className="text-[#0ABAB5] font-medium">{amount.toLocaleString()} L</span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Iter20 Task 9 — Reset test-data button removed (was added in Iter19 for QA;
              must NOT exist in production). */}
        </TabsContent>

        {/* Programs Tab */}
        <TabsContent value="programs" className="mt-4 space-y-3">
          {programs.map((program) => (
            <div key={program.program_id} className="p-4 rounded-2xl bg-[#121212] border border-white/5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{program.name_en}</p>
                  <p className="text-white/50 text-sm">{program.name_sq}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#0ABAB5] font-semibold">{program.price} L</span>
                  {isSuperAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditProgram(program)}
                      className="text-white/50 hover:text-white"
                    >
                      <PencilSimple size={18} />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </TabsContent>

        {/* Plans Tab — Iter31 FIX 6: full CRUD moved here from Owner Console. */}
        <TabsContent value="plans" className="mt-4 space-y-3" data-testid="admin-plans-content">
          <PlansManagerTab />
        </TabsContent>

        {/* Stafi Tab — Iter23 B1: Klientët sub-tab REMOVED, the CRM 'Klientët' top-level tab is canonical. */}
        {isSuperAdmin && (
          <TabsContent value="users" className="mt-4 space-y-3" data-testid="admin-staff-content">
            <p className="text-xs text-white/55 px-1" data-testid="admin-staff-count">
              {t('staffTab')} ({users.filter(u => u.role === 'staff' || u.role === 'location_manager').length})
            </p>
            {/* Add Staff shortcuts (Stafi sub-tab only) */}
            {userSubTab === 'staff' && (
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => navigate('/owner-console')}
                  className="h-11 bg-[#0ABAB5]/10 text-[#0ABAB5] border border-[#0ABAB5]/30 rounded-xl text-sm font-semibold"
                  data-testid="admin-add-manager"
                >
                  + {t('addManager')}
                </Button>
                <Button
                  onClick={() => navigate('/owner-console')}
                  className="h-11 bg-[#FF5A00]/10 text-[#FF5A00] border border-[#FF5A00]/30 rounded-xl text-sm font-semibold"
                  data-testid="admin-add-employee"
                >
                  + {t('addEmployee')}
                </Button>
              </div>
            )}

            {/* Bulk delete bar */}
            {selectedUserIds.size > 0 && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-between" data-testid="admin-users-bulkbar">
                <span className="text-xs text-red-300 font-semibold">{selectedUserIds.size} {t('selected')}</span>
                <Button
                  onClick={async () => {
                    if (!window.confirm(`${t('bulkDeleteConfirm')} (${selectedUserIds.size})?`)) return;
                    let failed = 0;
                    for (const uid of selectedUserIds) {
                      try { await axios.delete(`${API}/admin/users/${uid}`, { withCredentials: true }); }
                      catch (err) { failed += 1; console.warn('[Admin] delete user failed', uid, err?.response?.status); }
                    }
                    setSelectedUserIds(new Set());
                    fetchData();
                    if (failed) toast.error(`${failed} ✕`); else toast.success(t('autoSaved'));
                  }}
                  size="sm"
                  className="h-8 bg-red-500 text-white text-xs rounded-lg"
                  data-testid="admin-users-bulk-delete"
                >
                  <Trash size={14} className="mr-1" /> {t('bulkDeleteCta')}
                </Button>
              </div>
            )}

            {staffUsers.map((u) => (
              <div key={u.user_id} className="p-4 rounded-2xl bg-[#121212] border border-white/5" data-testid={`user-row-${u.user_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {u.user_id !== user?.user_id && u.role !== 'super_admin' && (
                      <input
                        type="checkbox"
                        checked={selectedUserIds.has(u.user_id)}
                        onChange={(e) => {
                          const next = new Set(selectedUserIds);
                          if (e.target.checked) next.add(u.user_id); else next.delete(u.user_id);
                          setSelectedUserIds(next);
                        }}
                        className="w-4 h-4 accent-[#0ABAB5]"
                        data-testid={`user-checkbox-${u.user_id}`}
                      />
                    )}
                    <div className="min-w-0">
                      <p className="font-medium truncate">{u.name}</p>
                      <p className="text-white/50 text-sm truncate">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isSuperAdmin && u.user_id !== user?.user_id ? (
                      <select
                        defaultValue={u.role || 'user'}
                        onChange={async (e) => {
                          try {
                            await axios.put(`${API}/admin/users/${u.user_id}/role?role=${e.target.value}`, {}, { withCredentials: true });
                            toast.success(t('autoSaved'));
                            fetchData();
                          } catch { toast.error('Failed'); }
                        }}
                        className="text-xs bg-[#0a0a0a] border border-white/10 rounded-lg px-2 py-1 text-white"
                        data-testid={`role-select-${u.user_id}`}
                      >
                        <option value="user">user</option>
                        <option value="staff">staff</option>
                        <option value="location_manager">manager</option>
                        <option value="super_admin">super_admin</option>
                      </select>
                    ) : (
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        u.role === 'super_admin' ? 'bg-[#FF5A00]/20 text-[#FF5A00]' :
                        u.role === 'location_manager' ? 'bg-[#FFD700]/20 text-[#FFD700]' :
                        u.role === 'staff' ? 'bg-[#0ABAB5]/20 text-[#0ABAB5]' :
                        'bg-white/10 text-white/50'
                      }`}>
                        {u.role?.replace('_', ' ')}
                      </span>
                    )}
                    {u.user_id !== user?.user_id && u.role !== 'super_admin' && (
                      <button
                        onClick={() => setUserToDelete(u)}
                        className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition"
                        title={t('deleteUserBtn')}
                        data-testid={`delete-user-${u.user_id}`}
                      >
                        <Trash size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>
        )}

        {/* Iter19 Task 10 — Subscribers tab */}
        {isSuperAdmin && (
          <TabsContent value="subscribers" className="mt-4">
            <AdminSubscribersTab />
          </TabsContent>
        )}

        {/* Marketing Tab — Owner or permitted Manager/Employee */}
        {canMarketing && (
          <TabsContent value="marketing" className="mt-4" data-testid="admin-marketing-content">
            <MarketingPanel />
          </TabsContent>
        )}

        {/* Iter22 — Customers CRM tab (Owner + permitted Manager). Iter23 B2 — shared component. */}
        {canCustomers && (
          <TabsContent value="customers" className="mt-4" data-testid="admin-customers-content">
            {/* Iter31 FIX 5 — Përdorues Total (all users, staff pinned first). */}
            <CustomersTab includeStaff />
          </TabsContent>
        )}

        {isSuperAdmin && (
          <TabsContent value="bookings" className="mt-4" data-testid="admin-bookings-content">
            <AdminBookingsTab />
          </TabsContent>
        )}
      </Tabs>

      {/* Edit Program Dialog */}
      <Dialog open={!!editProgram} onOpenChange={() => setEditProgram(null)}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle>Edit Program</DialogTitle>
          </DialogHeader>
          {editProgram && (
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-white/60 mb-2 block">Name (English)</label>
                <Input
                  value={editProgram.name_en}
                  onChange={(e) => setEditProgram({ ...editProgram, name_en: e.target.value })}
                  className="h-12 bg-[#0a0a0a] border-white/10 rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 mb-2 block">Name (Albanian)</label>
                <Input
                  value={editProgram.name_sq}
                  onChange={(e) => setEditProgram({ ...editProgram, name_sq: e.target.value })}
                  className="h-12 bg-[#0a0a0a] border-white/10 rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 mb-2 block">Price (Lek)</label>
                <Input
                  type="number"
                  value={editProgram.price}
                  onChange={(e) => setEditProgram({ ...editProgram, price: parseInt(e.target.value) || 0 })}
                  className="h-12 bg-[#0a0a0a] border-white/10 rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 mb-2 block">Duration (minutes)</label>
                <Input
                  type="number"
                  value={editProgram.duration_minutes}
                  onChange={(e) => setEditProgram({ ...editProgram, duration_minutes: parseInt(e.target.value) || 5 })}
                  className="h-12 bg-[#0a0a0a] border-white/10 rounded-xl"
                />
              </div>
              <Button
                onClick={handleUpdateProgram}
                className="w-full h-12 bg-[#0ABAB5] hover:bg-[#0ABAB5]/90 text-black font-semibold rounded-2xl"
              >
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Plan Dialog */}
      <Dialog open={!!editPlan} onOpenChange={() => setEditPlan(null)}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle>Edit Plan</DialogTitle>
          </DialogHeader>
          {editPlan && (
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-white/60 mb-2 block">Name (English)</label>
                <Input
                  value={editPlan.name_en}
                  onChange={(e) => setEditPlan({ ...editPlan, name_en: e.target.value })}
                  className="h-12 bg-[#0a0a0a] border-white/10 rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 mb-2 block">Name (Albanian)</label>
                <Input
                  value={editPlan.name_sq}
                  onChange={(e) => setEditPlan({ ...editPlan, name_sq: e.target.value })}
                  className="h-12 bg-[#0a0a0a] border-white/10 rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 mb-2 block">Price (Lek/month)</label>
                <Input
                  type="number"
                  value={editPlan.price}
                  onChange={(e) => setEditPlan({ ...editPlan, price: parseInt(e.target.value) || 0 })}
                  className="h-12 bg-[#0a0a0a] border-white/10 rounded-xl"
                />
              </div>
              {!editPlan.is_unlimited && (
                <div>
                  <label className="text-sm text-white/60 mb-2 block">Washes Included</label>
                  <Input
                    type="number"
                    value={editPlan.washes_included}
                    onChange={(e) => setEditPlan({ ...editPlan, washes_included: parseInt(e.target.value) || 0 })}
                    className="h-12 bg-[#0a0a0a] border-white/10 rounded-xl"
                  />
                </div>
              )}
              <Button
                onClick={handleUpdatePlan}
                className="w-full h-12 bg-[#0ABAB5] hover:bg-[#0ABAB5]/90 text-black font-semibold rounded-2xl"
              >
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete user confirmation */}
      <Dialog open={!!userToDelete} onOpenChange={() => !deleting && setUserToDelete(null)}>        <DialogContent className="bg-[#121212] border-white/10 max-w-sm mx-auto" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{t('deleteUserConfirmTitle')}</DialogTitle>
          </DialogHeader>
          {userToDelete && (
            <div className="space-y-4 py-2">
              <p className="text-white/70 text-sm">{t('deleteUserConfirmBody')}</p>
              <div className="p-3 rounded-xl bg-[#0a0a0a] border border-white/5">
                <p className="text-sm font-semibold text-white">{userToDelete.name}</p>
                <p className="text-xs text-white/40">{userToDelete.email}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  onClick={() => setUserToDelete(null)}
                  disabled={deleting}
                  className="h-11 rounded-xl border-white/15 bg-white/5 hover:bg-white/10 text-white text-sm"
                  data-testid="delete-user-cancel"
                >
                  {t('deleteUserCancel')}
                </Button>
                <Button
                  onClick={confirmDeleteUser}
                  disabled={deleting}
                  className="h-11 rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold text-sm"
                  data-testid="delete-user-confirm"
                >
                  {deleting ? '…' : t('deleteUserOK')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Iter20 Task 9 — ResetConfirm dialog removed alongside the button. */}
    </div>
  );
}
